// Firebase Google sign-in. Lazily initialized so MOCK mode never touches Firebase
// (no config needed offline). Produces the Firebase ID token the Realtime server's
// verifyToken expects — used as ?token= on the WS and Bearer on REST.
//
// SIM mode (VITE_SIM=1 — a compile-time constant, so prod builds drop these
// branches): auth runs against the local Firebase Auth EMULATOR and the sim user
// is signed in automatically with email/password instead of Google. The token
// plumbing is unchanged, so the local realtime receives a real emulator ID token.
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  type Auth,
  type User,
} from 'firebase/auth';
import { config } from './config';

let _auth: Auth | null = null;
function auth(): Auth {
  if (!_auth) {
    const app = initializeApp(config.firebase);
    _auth = getAuth(app);
    // SIM: must come right after getAuth(), before any other auth call.
    if (import.meta.env.VITE_SIM === '1') {
      connectAuthEmulator(_auth, import.meta.env.VITE_SIM_AUTH_EMULATOR || 'http://localhost:9099', { disableWarnings: true });
    }
  }
  return _auth;
}

export function signInWithGoogle() {
  return signInWithPopup(auth(), new GoogleAuthProvider());
}

export function signOut() {
  return fbSignOut(auth());
}

export function onAuthChange(cb: (user: User | null) => void) {
  if (import.meta.env.VITE_SIM === '1') return onSimAuthChange(cb);
  return onAuthStateChanged(auth(), cb);
}

export function getCurrentUser(): User | null {
  return auth().currentUser;
}

export async function getIdToken(): Promise<string | null> {
  const u = auth().currentUser;
  return u ? u.getIdToken() : null;
}

// SIM: whenever there is no user, sign the pre-created sim user in. The auth
// gate isn't told about the null while the first attempt is in flight (its blank
// splash holds — no Login flash). If that attempt fails, Login renders (its Google
// button still works: the emulator serves a fake-account popup) and sign-in is
// retried every few seconds until the emulator answers.
const SIM_SIGNIN_RETRY_MS = 3000;
function onSimAuthChange(cb: (user: User | null) => void) {
  const a = auth();
  const email = String(import.meta.env.VITE_SIM_USER_EMAIL || '');
  const password = String(import.meta.env.VITE_SIM_USER_PASSWORD || '');
  let live = true;
  let busy = false;
  let loginShown = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const attempt = () => {
    timer = null;
    if (!live || busy || a.currentUser) return;
    busy = true;
    signInWithEmailAndPassword(a, email, password)
      .catch((e: any) => {
        console.warn(`[sim] auto sign-in as ${email} failed (${e?.code || e?.message || e}); retrying in ${SIM_SIGNIN_RETRY_MS / 1000}s`);
        if (!loginShown) { loginShown = true; cb(null); }
        if (live && !a.currentUser && !timer) timer = setTimeout(attempt, SIM_SIGNIN_RETRY_MS);
      })
      .finally(() => { busy = false; });
  };
  const off = onAuthStateChanged(a, (u) => {
    if (!u) { attempt(); return; }
    if (timer) { clearTimeout(timer); timer = null; }
    loginShown = false;
    cb(u);
  });
  return () => {
    live = false;
    if (timer) clearTimeout(timer);
    off();
  };
}
